/**
 * MTMultiSelect - Multi-territory multi-select dropdown with an in-dropdown
 * search box. Modeled on India's CustomMultiSelect visual style but kept as a
 * separate MT-owned component so India code is never touched.
 *
 * Features:
 * - Checkbox multi-select with "Select All"
 * - Optional sticky search box at the top of the dropdown (searchable)
 * - Brand-styled focus border (orange, not the MUI default blue)
 */
import { KeyboardArrowDown, Search as SearchIcon } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = any

export type MTMultiSelectProps = {
  label?: string
  value?: string[]
  onChange: (e: { target: { value: string[] } }) => void
  options?: Option[]
  getOptionLabel?: (option: Option) => string
  getOptionValue?: (option: Option) => string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  /** When true, renders a sticky search box at the top to filter options. */
  searchable?: boolean
  searchPlaceholder?: string
}

export function MTMultiSelect({
  label,
  value = [],
  onChange,
  options = [],
  getOptionLabel = (option: Option) => option,
  getOptionValue = (option: Option) => option,
  placeholder = 'Select options',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  fullWidth = false,
  searchable = false,
  searchPlaceholder = 'Search',
}: MTMultiSelectProps) {
  const allValues = options.map(opt => getOptionValue(opt))
  const allSelected = value.length === allValues.length && allValues.length > 0

  const [searchTerm, setSearchTerm] = useState('')

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options
    const q = searchTerm.trim().toLowerCase()
    return options.filter(opt =>
      String(getOptionLabel(opt) ?? '')
        .toLowerCase()
        .includes(q)
    )
  }, [searchable, searchTerm, options, getOptionLabel])

  const handleChange = (e: { target: { value: unknown } }) => {
    const selected = e.target.value as string[]
    if (selected.includes('ALL')) {
      onChange({ target: { value: allSelected ? [] : allValues } })
      return
    }
    onChange({ target: { value: selected } })
  }

  // Full comma-joined labels of the current selection, for the native hover
  // tooltip on the (truncated) closed trigger.
  const selectedTitle =
    value && value.length > 0
      ? options
          .filter(opt => value.includes(getOptionValue(opt)))
          .map(opt => String(getOptionLabel(opt)))
          .join(', ')
      : undefined

  return (
    <Box
      sx={{
        minWidth: fullWidth ? '100%' : 225,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            mb: 1,
            lineHeight: '140%',
          }}
        >
          {label}
          {required && (
            <span style={{ color: 'var(--forbrand-error)' }}>*</span>
          )}
        </Typography>
      )}
      <FormControl fullWidth size="small" disabled={disabled} error={error}>
        <Tooltip
          title={selectedTitle || ''}
          arrow
          placement="top"
          disableInteractive
        >
          <Select
            multiple
            value={value || []}
            onChange={handleChange}
            IconComponent={KeyboardArrowDown}
            displayEmpty
            // Fallback native title; wrapping MUI Tooltip above is the primary.
            SelectDisplayProps={{ title: selectedTitle }}
            renderValue={selected => {
              const sel = selected as string[]
              if (!sel || sel.length === 0) {
                return <span style={{ color: '#999' }}>{placeholder}</span>
              }
              const selectedLabels = options
                .filter(opt => sel.includes(getOptionValue(opt)))
                .map(opt => getOptionLabel(opt))
              const displayText = selectedLabels.join(', ')
              return (
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayText}
                </span>
              )
            }}
            sx={{
              backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
              fontSize: '14px',
              height: '48px',
              width: fullWidth ? '100%' : '225px',
              borderRadius: 'unset',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: error
                  ? 'var(--forbrand-error)'
                  : 'var(--forbrand-color)',
              },
              '& .MuiSelect-select': {
                fontSize: '14px',
                color: '#374151',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiSelect-icon': { color: '#6b7280', fontSize: '20px' },
              '&.Mui-disabled': { backgroundColor: 'var(--bg-disabled)' },
            }}
            onClose={() => setSearchTerm('')}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                sx: {
                  boxShadow:
                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                  border: '1px solid var(--border-strong)',
                  mt: 0.5,
                  maxHeight: '250px',
                  width: '225px',
                },
              },
            }}
          >
            {searchable && (
              <ListSubheader
                sx={{ p: 1, backgroundColor: '#fff', lineHeight: 'normal' }}
                onKeyDown={e => e.stopPropagation()}
              >
                <TextField
                  size="small"
                  autoFocus
                  fullWidth
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: '#6b7280' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '14px' },
                    // Brand-styled border (orange focus) instead of MUI blue.
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: '#d0d0d0',
                      },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: '#a0a0a0',
                      },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: 'var(--forbrand-color)',
                        borderWidth: '1px',
                      },
                  }}
                />
              </ListSubheader>
            )}

            {!(searchable && searchTerm.trim()) && (
              <MenuItem value="ALL">
                <Checkbox
                  sx={{
                    color: 'var(--forbrand-color)',
                    '&.Mui-checked': { color: 'var(--forbrand-color)' },
                    '&.MuiCheckbox-indeterminate': {
                      color: 'var(--forbrand-color)',
                    },
                  }}
                  checked={allSelected}
                  indeterminate={
                    value.length > 0 && value.length < options.length
                  }
                />
                <ListItemText
                  primary="Select All"
                  sx={{ '.MuiTypography-root': { fontSize: '14px' } }}
                />
              </MenuItem>
            )}

            {filteredOptions.map((option, index) => (
              <MenuItem
                key={index}
                value={getOptionValue(option)}
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                <Checkbox
                  sx={{
                    color: 'var(--forbrand-color)',
                    '&.Mui-checked': { color: 'var(--forbrand-color)' },
                  }}
                  checked={value.includes(getOptionValue(option))}
                />
                <ListItemText
                  primary={getOptionLabel(option)}
                  sx={{ '.MuiTypography-root': { fontSize: '14px' } }}
                />
              </MenuItem>
            ))}

            {searchable &&
              searchTerm.trim() &&
              filteredOptions.length === 0 && (
                <MenuItem disabled sx={{ fontSize: '14px', color: '#999' }}>
                  No matches found
                </MenuItem>
              )}
          </Select>
        </Tooltip>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? 'var(--forbrand-error)' : '#666',
              mt: 0.5,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  )
}

export default MTMultiSelect
